/* product-page.js */

document.addEventListener("DOMContentLoaded", () => {
  initGallery();
  initLightbox();
  initStickyAtc();
  initVariantPicker();
  initQtyStepper();
  initFitModals();
  loadAsyncSections();
});

function initGallery() {
  const gallery = document.querySelector("[data-product-gallery]");
  if (!gallery) return;
  const thumbs = gallery.querySelectorAll("[data-gallery-thumb]");
  const triggers = [...gallery.querySelectorAll("[data-gallery-zoom]")];
  const track = gallery.querySelector("[data-gallery-track]");
  const thumbsCol = gallery.querySelector(".gallery-thumbs-col");
  const zoomChip = gallery.querySelector("[data-gallery-zoom-chip]");
  const mobileMq = window.matchMedia("(max-width: 860px)");
  let current = 0;
  let scrollingTo = false;

  const setActive = (index) => {
    if (index < 0 || index >= triggers.length) return;
    current = index;
    triggers.forEach((t, i) => {
      const on = i === index;
      t.classList.toggle("is-active", on);
      const img = t.querySelector("[data-gallery-slide]");
      if (img) img.classList.toggle("active", on);
    });
    thumbs.forEach((t, i) => {
      const on = i === index;
      t.classList.toggle("active", on);
      if (on && thumbsCol && !mobileMq.matches) {
        const thumbTop = t.offsetTop;
        const visible =
          thumbTop >= thumbsCol.scrollTop &&
          thumbTop + t.offsetHeight <= thumbsCol.scrollTop + thumbsCol.clientHeight;
        if (!visible) {
          t.scrollIntoView({ block: "nearest", behavior: "smooth" });
        }
      }
    });
  };

  const scrollToSlide = (index) => {
    const slide = triggers[index];
    if (!slide) return;
    setActive(index);

    if (mobileMq.matches && track) {
      const isVerticalSnap = track.scrollHeight > track.clientHeight + 10;
      if (isVerticalSnap) {
        track.scrollTo({ top: track.clientHeight * index, behavior: "smooth" });
        return;
      }
      const isHorizontalSwipe = track.scrollWidth > track.clientWidth + 10;
      if (isHorizontalSwipe) {
        track.scrollTo({ left: track.clientWidth * index, behavior: "smooth" });
        return;
      }
    }

    // Desktop: scroll page so the stacked image comes into view
    scrollingTo = true;
    const headerOffset = 90;
    const top = slide.getBoundingClientRect().top + window.scrollY - headerOffset;
    window.scrollTo({ top, behavior: "smooth" });
    window.setTimeout(() => {
      scrollingTo = false;
    }, 600);
  };

  thumbs.forEach((thumb) => {
    thumb.addEventListener("click", () => {
      scrollToSlide(parseInt(thumb.dataset.index, 10) || 0);
    });
  });

  // Mobile gallery: sync active index while scrolling inside the track
  const syncTrackEdges = () => {
    if (!track || !mobileMq.matches) return;
    if (track.scrollHeight <= track.clientHeight + 10) {
      track.classList.add("is-at-end");
      track.classList.add("is-at-start");
      return;
    }
    const atStart = track.scrollTop <= 1;
    const atEnd = track.scrollTop + track.clientHeight >= track.scrollHeight - 2;
    track.classList.toggle("is-at-start", atStart);
    track.classList.toggle("is-at-end", atEnd);
  };

  track?.addEventListener(
    "scroll",
    () => {
      if (!mobileMq.matches) return;
      let index = current;
      if (track.scrollHeight > track.clientHeight + 10) {
        index = Math.round(track.scrollTop / Math.max(track.clientHeight, 1));
        syncTrackEdges();
      } else if (track.scrollWidth > track.clientWidth + 10) {
        index = Math.round(track.scrollLeft / Math.max(track.clientWidth, 1));
      }
      if (index !== current && index >= 0 && index < triggers.length) {
        setActive(index);
      }
    },
    { passive: true }
  );

  // Image area = scroll images; after last image, continue into description sheet
  if (track) {
    let touchStartY = 0;
    let moved = false;
    syncTrackEdges();

    track.addEventListener(
      "touchstart",
      (e) => {
        touchStartY = e.touches[0]?.clientY || 0;
        moved = false;
      },
      { passive: true }
    );

    track.addEventListener(
      "touchmove",
      (e) => {
        if (!mobileMq.matches) return;
        const y = e.touches[0]?.clientY || 0;
        const dy = touchStartY - y;
        if (Math.abs(dy) > 6) moved = true;
        if (track.scrollHeight <= track.clientHeight + 10) return;

        const atEnd = track.scrollTop + track.clientHeight >= track.scrollHeight - 2;
        const atStart = track.scrollTop <= 1;

        // Last image reached → page scroll brings description over images
        if (atEnd && dy > 0) {
          track.classList.add("is-at-end");
          window.scrollBy({ top: dy, left: 0, behavior: "auto" });
          touchStartY = y;
        } else if (atStart && dy < 0 && window.scrollY > 0) {
          track.classList.add("is-at-start");
          window.scrollBy({ top: dy, left: 0, behavior: "auto" });
          touchStartY = y;
        }
      },
      { passive: true }
    );

    // Avoid opening zoom if the user was scrolling images
    triggers.forEach((trigger) => {
      trigger.addEventListener(
        "click",
        (e) => {
          if (mobileMq.matches && moved) {
            e.preventDefault();
            e.stopImmediatePropagation();
            moved = false;
          }
        },
        true
      );
      trigger.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          trigger.click();
        }
      });
    });
  }

  // Desktop: highlight thumb for the image currently in view while scrolling
  if (triggers.length && "IntersectionObserver" in window) {
    const observer = new IntersectionObserver(
      (entries) => {
        if (mobileMq.matches || scrollingTo) return;
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (!visible) return;
        const index = triggers.indexOf(visible.target);
        if (index !== -1 && index !== current) setActive(index);
      },
      { root: null, rootMargin: "-20% 0px -45% 0px", threshold: [0.2, 0.4, 0.6] }
    );
    triggers.forEach((slide) => observer.observe(slide));
  }

  zoomChip?.addEventListener("click", (e) => {
    e.preventDefault();
    e.stopPropagation();
    triggers[current]?.click();
  });
}

function initFitModals() {
  const modals = document.querySelectorAll("[data-fit-modal]");
  if (!modals.length) return;

  const open = (name) => {
    modals.forEach((modal) => {
      const match = modal.dataset.fitModal === name;
      modal.hidden = !match;
      if (match) document.body.style.overflow = "hidden";
    });
  };

  const close = () => {
    modals.forEach((modal) => {
      modal.hidden = true;
    });
    document.body.style.overflow = "";
  };

  document.querySelectorAll("[data-fit-open]").forEach((btn) => {
    btn.addEventListener("click", () => open(btn.dataset.fitOpen));
  });
  document.querySelectorAll("[data-fit-close]").forEach((btn) => {
    btn.addEventListener("click", close);
  });
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") close();
  });

  const hash = (window.location.hash || "").replace("#", "");
  if (hash === "size-chart") open(hash);
}

function initLightbox() {
  const modal = document.querySelector("[data-lightbox]");
  if (!modal) return;
  const triggers = document.querySelectorAll("[data-gallery-zoom]");
  const images = modal.querySelectorAll("[data-lightbox-img]");
  const MAX_ZOOM = 3;
  let current = 0;
  let historyOpen = false;
  let zoom = 1;

  document.body.appendChild(modal);

  // Remove any Dawn hover-magnify overlays that can appear over the gallery
  document.querySelectorAll(".image-magnify-full-size").forEach((el) => el.remove());

  function activeImg() {
    return images[current] || null;
  }

  function resetZoom() {
    zoom = 1;
    images.forEach((img) => {
      img.style.transform = "";
      img.style.transformOrigin = "center center";
      img.classList.remove("is-zoomed");
    });
  }

  function show(index) {
    if (!images.length) return;
    resetZoom();
    current = (index + images.length) % images.length;
    images.forEach((img, i) => {
      img.hidden = i !== current;
    });
  }

  function isOpen() {
    return !modal.hasAttribute("hidden");
  }

  function open(index) {
    show(index);
    modal.hidden = false;
    document.body.classList.add("lightbox-open");
    document.body.style.overflow = "hidden";
    if (!historyOpen) {
      history.pushState({ lightbox: true }, "");
      historyOpen = true;
    }
  }

  function hide() {
    if (!isOpen()) return;
    resetZoom();
    modal.hidden = true;
    document.body.classList.remove("lightbox-open");
    document.body.style.overflow = "";
  }

  function close() {
    if (!isOpen()) return;
    hide();
    if (historyOpen) {
      historyOpen = false;
      history.back();
    }
  }

  function toggleZoom(e) {
    const img = activeImg();
    if (!img) return;
    if (zoom >= MAX_ZOOM) {
      resetZoom();
      return;
    }
    const rect = img.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / Math.max(rect.width, 1)) * 100;
    const y = ((e.clientY - rect.top) / Math.max(rect.height, 1)) * 100;
    zoom = MAX_ZOOM;
    img.style.transformOrigin = `${x}% ${y}%`;
    img.style.transform = `scale(${MAX_ZOOM})`;
    img.classList.add("is-zoomed");
  }

  triggers.forEach((trigger) => {
    trigger.addEventListener("click", () => {
      open(parseInt(trigger.dataset.index, 10) || 0);
    });
  });

  modal.querySelectorAll("[data-lightbox-close]").forEach((btn) => {
    btn.addEventListener("click", (e) => {
      e.stopPropagation();
      close();
    });
  });
  modal.querySelector("[data-lightbox-prev]")?.addEventListener("click", (e) => {
    e.stopPropagation();
    show(current - 1);
  });
  modal.querySelector("[data-lightbox-next]")?.addEventListener("click", (e) => {
    e.stopPropagation();
    show(current + 1);
  });

  images.forEach((img) => {
    img.addEventListener("click", (e) => {
      e.stopPropagation();
      toggleZoom(e);
    });
  });

  // Close via X / white area / Escape — image click zooms (Alkaram)
  modal.addEventListener("click", (e) => {
    if (e.target === modal || e.target.hasAttribute("data-lightbox-close")) close();
  });

  document.addEventListener("keydown", (e) => {
    if (!isOpen()) return;
    if (e.key === "Escape") {
      if (zoom > 1) resetZoom();
      else close();
    }
    if (e.key === "ArrowLeft") show(current - 1);
    if (e.key === "ArrowRight") show(current + 1);
  });

  window.addEventListener("popstate", () => {
    if (!isOpen()) {
      historyOpen = false;
      return;
    }
    historyOpen = false;
    hide();
  });
}

function initStickyAtc() {
  const bar = document.querySelector("[data-sticky-atc]");
  const anchor = document.querySelector("[data-add-to-cart]");
  const stickyBtn = bar?.querySelector("[data-sticky-add]");
  if (!bar || !anchor) return;

  // Show only after scrolling past the main ATC (not while still above it / on images)
  const sync = () => {
    const rect = anchor.getBoundingClientRect();
    const scrolledPast = rect.bottom < 8;
    bar.hidden = anchor.disabled || !scrolledPast;
  };

  const observer = new IntersectionObserver(() => sync(), {
    threshold: [0, 0.01, 1],
    rootMargin: "0px 0px -8px 0px",
  });
  observer.observe(anchor);
  window.addEventListener("scroll", sync, { passive: true });
  window.addEventListener("resize", sync, { passive: true });
  sync();

  stickyBtn?.addEventListener("click", () => {
    if (anchor.disabled) return;
    anchor.click();
  });

  const mo = new MutationObserver(sync);
  mo.observe(anchor, { attributes: true, attributeFilter: ["disabled"] });
}

function initVariantPicker() {
  const form = document.querySelector("[data-product-form]");
  if (!form) return;

  const variantInput = form.querySelector("[data-variant-id]");
  const priceEl = document.querySelector("[data-product-price]");
  const stickyPriceEl = document.querySelector("[data-sticky-price]");
  const stickyBtn = document.querySelector("[data-sticky-add]");
  const skuEl = document.querySelector("[data-sku]");
  const stockEl = document.querySelector("[data-stock-status]");
  const stockVariantsEl = document.querySelector("[data-stock-variants]");
  const stockVariants = stockVariantsEl ? JSON.parse(stockVariantsEl.textContent) : {};
  const addBtn = form.querySelector("[data-add-to-cart]");
  const restockForm = document.querySelector("[data-restock-form]");
  const optionGroups = form.querySelectorAll("[data-option-index]");

  // Product JSON is needed for real variant matching — read it from the
  // handle and fetch once, cached for the life of the page.
  const handle = document.body.dataset.productHandle || document.querySelector("[data-product-handle]")?.dataset.productHandle;
  let productData = null;

  fetch(`/products/${handle}.js`).then(r => r.json()).then(data => {
    productData = data;
    wireOptions();
  });

  function wireOptions() {
    const selections = {};

    optionGroups.forEach(group => {
      const idx = group.dataset.optionIndex;
      const buttons = group.querySelectorAll(".qv-option-btn, .swatch-btn");
      buttons[0]?.classList.add("selected");
      selections[idx] = buttons[0]?.dataset.value;

      buttons.forEach(btn => {
        btn.addEventListener("click", () => {
          buttons.forEach(b => b.classList.remove("selected"));
          btn.classList.add("selected");
          selections[idx] = btn.dataset.value;
          const selectedLabel = group.querySelector("[data-selected-option]");
          if (selectedLabel) selectedLabel.textContent = btn.dataset.value;
          updateVariant(selections);
        });
      });
    });

    updateVariant(selections);
  }

  function updateVariant(selections) {
    if (!productData) return;
    const match = productData.variants.find(v =>
      Object.keys(selections).every(i => v[`option${parseInt(i) + 1}`] === selections[i])
    );
    if (!match) return;

    variantInput.value = match.id;
    if (skuEl) skuEl.textContent = match.sku || "";

    const money = (cents) => {
      const n = Math.round(Number(cents || 0) / 100);
      return `PKR ${n.toLocaleString("en-US")}`;
    };
    const onSale = match.compare_at_price > match.price;
    const savings = onSale ? Math.round((match.compare_at_price - match.price) / 100).toLocaleString("en-US") : "";
    const priceHtml = `${onSale ? `<span class="alk-price-label">Sale price</span><span class="new">${money(match.price)}</span><span class="old">${money(match.compare_at_price)}</span><span class="save-tag">SAVE RS.${savings}</span>` : `<span class="new">${money(match.price)}</span>`}`;
    if (priceEl) priceEl.innerHTML = priceHtml;
    if (stickyPriceEl) stickyPriceEl.innerHTML = `${onSale ? `<span class="old">${money(match.compare_at_price)}</span>` : ""}<span class="new">${money(match.price)}</span>`;

    if (match.available) {
      const variantStock = stockVariants[match.id];
      const quantity = Number(variantStock?.quantity);
      const lowStock = variantStock?.tracked && quantity > 0 && quantity <= 10;
      if (stockEl) {
        stockEl.innerHTML = lowStock
          ? `<span class="stock-status__icon" aria-hidden="true"><svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M13.4 2.5 5.8 13h5.4l-.7 8.5L18.3 11h-5.4l.5-8.5Z" fill="currentColor"/></svg></span><span class="stock-status__copy"><strong>Only ${quantity} left</strong><span>Selling fast — order now to avoid missing out</span></span>`
          : "In stock";
        stockEl.classList.toggle("low-stock", lowStock);
      }
      stockEl?.classList.remove("out-of-stock");
      addBtn.disabled = false;
      addBtn.textContent = "Add to cart";
      if (stickyBtn) {
        stickyBtn.disabled = false;
        stickyBtn.textContent = "Add to cart";
      }
      if (restockForm) restockForm.hidden = true;
    } else {
      if (stockEl) {
        stockEl.textContent = "Out of stock";
        stockEl.classList.remove("low-stock");
        stockEl.classList.add("out-of-stock");
      }
      addBtn.disabled = true;
      addBtn.textContent = "Out of stock";
      if (stickyBtn) {
        stickyBtn.disabled = true;
        stickyBtn.textContent = "Out of stock";
      }
      if (restockForm) restockForm.hidden = false;
    }
  }

  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    addBtn.textContent = "Adding…";
    try {
      const res = await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          id: variantInput.value,
          quantity: parseInt(form.querySelector("[data-qty-input]")?.value, 10) || 1,
        }),
      });
      if (!res.ok) throw new Error("Add to cart failed");
      await res.json();
      document.dispatchEvent(new CustomEvent("cart:updated"));
      if (typeof window.openCartDrawer === "function") window.openCartDrawer();
      addBtn.textContent = "Added ✓";
      setTimeout(() => {
        addBtn.textContent = "Add to cart";
      }, 1500);
    } catch (err) {
      addBtn.textContent = "Try again";
      console.error(err);
    }
  });
}

function initQtyStepper() {
  document.querySelectorAll(".qty-stepper").forEach(stepper => {
    const input = stepper.querySelector("[data-qty-input]");
    stepper.querySelector("[data-qty-increase]")?.addEventListener("click", () => {
      input.value = Math.max(1, parseInt(input.value || 1, 10) + 1);
    });
    stepper.querySelector("[data-qty-decrease]")?.addEventListener("click", () => {
      input.value = Math.max(1, parseInt(input.value || 1, 10) - 1);
    });
  });
}

/* Loads sections async via the Section Rendering API — used for product
   recommendations so they don't block the initial product page render. */
function loadAsyncSections() {
  document.querySelectorAll("[data-section-loader]").forEach(async (el) => {
    try {
      const res = await fetch(el.dataset.url);
      const html = await res.text();
      const wrapper = document.createElement("div");
      wrapper.innerHTML = html;
      el.replaceWith(...wrapper.childNodes);
    } catch (err) {
      console.error("Failed to load section", err);
    }
  });
}
